import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Upload, ArrowRight, ArrowLeft, MessageCircle, TreePine, Download, Share2, Plus, ExternalLink, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceTypeOptions = [
  'Memorial Service',
  'Bhog and Ardas',
  'Tehrvi',
  'Funeral Service',
  'Celebration of Life',
  'Other'
];

export default function CreateMemorial({ user }) {
  const navigate = useNavigate();
  const [view, setView] = useState('loading'); // 'loading', 'list', 'create'
  const [myMemorials, setMyMemorials] = useState([]);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    deceased_name: '',
    deceased_photo: '',
    birth_date: '',
    death_date: '',
    service_date: '',
    service_type: '',
    custom_service_type: '',
    host_message: ''
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdMemorial, setCreatedMemorial] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);

  useEffect(() => {
    fetchMyMemorials();
  }, []);

  const fetchMyMemorials = async () => {
    try {
      const response = await axios.get(`${API}/memorials/my`, {
        withCredentials: true
      });
      setMyMemorials(response.data);
      setView('list');
    } catch (error) {
      console.error('Error fetching memorials:', error);
      setView('list');
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setFormData({ ...formData, deceased_photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const generateShareableImage = async (memorial) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = 1080;
    canvas.height = 1350;
    
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#f5f5f0');
    gradient.addColorStop(1, '#e8e4d9');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.strokeStyle = '#556B2F';
    ctx.lineWidth = 3;
    ctx.strokeRect(40, 40, canvas.width - 80, canvas.height - 80);
    
    ctx.strokeStyle = '#556B2F40';
    ctx.lineWidth = 1;
    ctx.strokeRect(60, 60, canvas.width - 120, canvas.height - 120);
    
    const serviceType = memorial.service_type || 'In Loving Memory';
    ctx.fillStyle = '#556B2F';
    ctx.font = 'italic 32px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.fillText(serviceType, canvas.width / 2, 140);
    
    ctx.beginPath();
    ctx.moveTo(canvas.width / 2 - 100, 165);
    ctx.lineTo(canvas.width / 2 + 100, 165);
    ctx.strokeStyle = '#556B2F';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const photoSize = 400;
        const photoX = (canvas.width - photoSize) / 2;
        const photoY = 220;
        
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 20;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 10;
        
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(photoX - 10, photoY - 10, photoSize + 20, photoSize + 20);
        
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        
        const scale = Math.max(photoSize / img.width, photoSize / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const offsetX = (scaledWidth - photoSize) / 2;
        const offsetY = (scaledHeight - photoSize) / 2;
        
        ctx.save();
        ctx.beginPath();
        ctx.rect(photoX, photoY, photoSize, photoSize);
        ctx.clip();
        ctx.drawImage(img, photoX - offsetX, photoY - offsetY, scaledWidth, scaledHeight);
        ctx.restore();
        
        ctx.fillStyle = '#2d3a1f';
        ctx.font = 'bold 56px Georgia, serif';
        ctx.textAlign = 'center';
        ctx.fillText(memorial.deceased_name, canvas.width / 2, 720);
        
        let datesText = '';
        if (memorial.birth_date && memorial.death_date) {
          datesText = `${formatDate(memorial.birth_date)} — ${formatDate(memorial.death_date)}`;
        } else if (memorial.birth_date) {
          datesText = `Born: ${formatDate(memorial.birth_date)}`;
        } else if (memorial.death_date) {
          datesText = `${formatDate(memorial.death_date)}`;
        }
        
        if (datesText) {
          ctx.fillStyle = '#556B2F';
          ctx.font = '28px Georgia, serif';
          ctx.fillText(datesText, canvas.width / 2, 780);
        }
        
        if (memorial.service_date) {
          ctx.fillStyle = '#666666';
          ctx.font = '24px Georgia, serif';
          ctx.fillText(`Service: ${formatDate(memorial.service_date)}`, canvas.width / 2, 830);
        }
        
        ctx.fillStyle = '#556B2F';
        ctx.font = '48px Georgia, serif';
        ctx.fillText('❦', canvas.width / 2, 920);
        
        if (memorial.host_message) {
          ctx.fillStyle = '#555555';
          ctx.font = 'italic 22px Georgia, serif';
          const maxWidth = canvas.width - 160;
          const lines = wrapText(ctx, `"${memorial.host_message}"`, maxWidth);
          let y = 980;
          lines.slice(0, 4).forEach((line, i) => {
            ctx.fillText(line, canvas.width / 2, y + (i * 32));
          });
        }
        
        ctx.fillStyle = '#556B2F80';
        ctx.font = '20px Georgia, serif';
        ctx.fillText('sarmoh.in', canvas.width / 2, canvas.height - 80);
        
        const dataUrl = canvas.toDataURL('image/png');
        setGeneratedImage(dataUrl);
        resolve(dataUrl);
      };
      
      img.onerror = () => {
        ctx.fillStyle = '#556B2F20';
        ctx.fillRect((canvas.width - 400) / 2, 220, 400, 400);
        
        ctx.fillStyle = '#2d3a1f';
        ctx.font = 'bold 56px Georgia, serif';
        ctx.textAlign = 'center';
        ctx.fillText(memorial.deceased_name, canvas.width / 2, 720);
        
        const dataUrl = canvas.toDataURL('image/png');
        setGeneratedImage(dataUrl);
        resolve(dataUrl);
      };
      
      img.src = memorial.deceased_photo;
    });
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  const wrapText = (ctx, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  const handleSubmit = async () => {
    if (!formData.deceased_name || !formData.deceased_photo) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);

    try {
      const serviceType = formData.service_type === 'Other' 
        ? formData.custom_service_type 
        : formData.service_type;

      const response = await axios.post(`${API}/memorial/create`, {
        deceased_name: formData.deceased_name,
        deceased_photo: formData.deceased_photo,
        birth_date: formData.birth_date || null,
        death_date: formData.death_date || null,
        service_date: formData.service_date || null,
        service_type: serviceType || null,
        host_message: formData.host_message || null
      }, {
        withCredentials: true
      });

      const memorial = response.data;
      setCreatedMemorial(memorial);
      
      await generateShareableImage({
        ...memorial,
        deceased_photo: formData.deceased_photo,
        service_type: serviceType
      });
      
      setStep(4);
      toast.success('Memorial created successfully!');
    } catch (error) {
      console.error('Error creating memorial:', error);
      toast.error('Failed to create memorial');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadImage = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.download = `${formData.deceased_name.replace(/\s+/g, '_')}_memorial.png`;
    link.href = generatedImage;
    link.click();
  };

  const copyLink = (memorialId) => {
    const link = `${window.location.origin}/tribute/${memorialId}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  };

  const startNewMemorial = () => {
    setFormData({
      deceased_name: '',
      deceased_photo: '',
      birth_date: '',
      death_date: '',
      service_date: '',
      service_type: '',
      custom_service_type: '',
      host_message: ''
    });
    setPhotoPreview('');
    setStep(1);
    setCreatedMemorial(null);
    setGeneratedImage(null);
    setView('create');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="sticky top-0 z-50 glass-morphism border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <a href="/" className="flex items-center space-x-3">
              <TreePine className="w-8 h-8 text-primary" strokeWidth={1.5} />
              <h1 className="text-2xl font-serif font-semibold text-foreground">Sarmoh</h1>
            </a>
            
            <div className="flex items-center space-x-1 bg-paper rounded-full p-1">
              <button
                className="px-6 py-2 rounded-full text-sm font-serif bg-primary text-primary-foreground"
                data-testid="tab-memorial"
              >
                <Heart className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
                Memorial Tribute
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 rounded-full text-sm font-serif text-foreground-muted hover:bg-white/50 transition-colors"
                data-testid="tab-family-tree"
              >
                <TreePine className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
                Family Tree
              </button>
            </div>
            
            {user && (
              <div className="flex items-center space-x-3">
                <span className="text-sm font-sans text-foreground-muted">{user.name}</span>
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                )}
              </div>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Loading State */}
        {view === 'loading' && (
          <div className="flex items-center justify-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          </div>
        )}

        {/* Memorial List View */}
        {view === 'list' && (
          <div className="space-y-8">
            <div className="text-center">
              <Heart className="w-12 h-12 text-primary mx-auto mb-4" strokeWidth={1.5} />
              <h1 className="text-4xl font-serif tracking-tight text-foreground mb-2">
                My Memorial Tributes
              </h1>
              <p className="text-base font-sans text-foreground-muted">
                Manage your memorial tribute pages
              </p>
            </div>

            {/* Create New Button */}
            <div className="flex justify-center">
              <Button
                onClick={startNewMemorial}
                data-testid="create-new-memorial-btn"
                className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-8 py-3 font-serif tracking-wide"
              >
                <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Create New Memorial
              </Button>
            </div>

            {/* Existing Memorials */}
            {myMemorials.length > 0 ? (
              <div className="grid gap-6">
                {myMemorials.map((memorial) => (
                  <div
                    key={memorial.memorial_id}
                    className="bg-white border border-border shadow-card rounded-lg p-6 hover:shadow-md transition-shadow"
                    data-testid={`memorial-card-${memorial.memorial_id}`}
                  >
                    <div className="flex items-start space-x-6">
                      {/* Photo */}
                      <div className="flex-shrink-0">
                        <img
                          src={memorial.deceased_photo}
                          alt={memorial.deceased_name}
                          className="w-24 h-24 rounded-lg object-cover border-2 border-primary/20"
                        />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-2xl font-serif text-foreground mb-1">
                          {memorial.deceased_name}
                        </h3>
                        {memorial.service_type && (
                          <p className="text-sm font-sans text-primary mb-2">
                            {memorial.service_type}
                          </p>
                        )}
                        <div className="flex items-center space-x-4 text-sm text-foreground-muted font-sans">
                          {(memorial.birth_date || memorial.death_date) && (
                            <span>
                              {memorial.birth_date && formatDate(memorial.birth_date)}
                              {memorial.birth_date && memorial.death_date && ' — '}
                              {memorial.death_date && formatDate(memorial.death_date)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 mt-2 text-sm text-muted-foreground">
                          <Users className="w-4 h-4" strokeWidth={1.5} />
                          <span>{memorial.tribute_count || 0} tributes</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col space-y-2">
                        <Button
                          onClick={() => navigate(`/tribute/${memorial.memorial_id}`)}
                          className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-4 py-2 text-sm font-serif"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} />
                          View Page
                        </Button>
                        <Button
                          onClick={() => copyLink(memorial.memorial_id)}
                          variant="outline"
                          className="border border-primary/20 text-primary hover:bg-primary/5 rounded-full px-4 py-2 text-sm font-serif"
                        >
                          <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                          Copy Link
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-paper border border-border rounded-lg p-12 text-center">
                <Heart className="w-16 h-16 text-primary/30 mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-serif text-foreground mb-2">No memorials yet</h3>
                <p className="text-foreground-muted font-sans mb-6">
                  Create your first memorial tribute to honor a loved one
                </p>
                <Button
                  onClick={startNewMemorial}
                  className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-8 py-3 font-serif tracking-wide"
                >
                  <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Create Memorial
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Create Memorial Form */}
        {view === 'create' && step < 4 && (
          <>
            <div className="text-center mb-8">
              <button
                onClick={() => setView('list')}
                className="text-sm text-primary hover:underline font-sans mb-4 inline-block"
              >
                ← Back to My Memorials
              </button>
              <Heart className="w-12 h-12 text-primary mx-auto mb-4" strokeWidth={1.5} />
              <h1 className="text-4xl font-serif tracking-tight text-foreground mb-2">
                Create a Memorial Tribute
              </h1>
              <p className="text-base font-sans text-foreground-muted">
                Honor your loved one's memory with a beautiful tribute page
              </p>
            </div>

            <div className="flex justify-center mb-8">
              <div className="flex items-center space-x-2">
                {[1, 2, 3].map((s) => (
                  <React.Fragment key={s}>
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-serif ${
                        step >= s
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-paper text-muted-foreground border border-border'
                      }`}
                    >
                      {s}
                    </div>
                    {s < 3 && (
                      <div className={`w-12 h-0.5 ${step > s ? 'bg-primary' : 'bg-border'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="bg-white border border-border shadow-card rounded-lg p-8">
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="font-sans text-foreground mb-2 block">
                      What was your loved one's name? *
                    </Label>
                    <Input
                      id="name"
                      value={formData.deceased_name}
                      onChange={(e) => setFormData({ ...formData, deceased_name: e.target.value })}
                      data-testid="deceased-name-input"
                      className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-3 font-sans text-lg"
                      placeholder="Enter their full name"
                      required
                    />
                  </div>

                  <div>
                    <Label className="font-sans text-foreground mb-3 block">
                      Upload a cherished photo *
                    </Label>
                    <div className="space-y-4">
                      {photoPreview && (
                        <div className="flex justify-center">
                          <img
                            src={photoPreview}
                            alt="Preview"
                            className="w-48 h-48 rounded-lg object-cover border-4 border-primary shadow-lg"
                          />
                        </div>
                      )}
                      <div className="flex flex-col items-center space-y-3">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          data-testid="photo-upload"
                          className="hidden"
                          id="photo-upload"
                        />
                        <label htmlFor="photo-upload">
                          <Button
                            type="button"
                            variant="outline"
                            className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-6 py-2 font-serif cursor-pointer"
                            onClick={() => document.getElementById('photo-upload').click()}
                          >
                            <Upload className="w-5 h-5 mr-2" strokeWidth={1.5} />
                            Choose Photo
                          </Button>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button
                      onClick={() => {
                        if (!formData.deceased_name || !formData.deceased_photo) {
                          toast.error('Please complete all required fields');
                          return;
                        }
                        setStep(2);
                      }}
                      data-testid="next-step-1"
                      className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-8 py-3 font-serif tracking-wide"
                    >
                      Continue
                      <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="birth_date" className="font-sans text-foreground mb-2 block">
                        Birth Date
                      </Label>
                      <Input
                        id="birth_date"
                        type="date"
                        value={formData.birth_date}
                        onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                        data-testid="birth-date-input"
                        className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-2 font-sans"
                      />
                    </div>
                    <div>
                      <Label htmlFor="death_date" className="font-sans text-foreground mb-2 block">
                        Date of Passing
                      </Label>
                      <Input
                        id="death_date"
                        type="date"
                        value={formData.death_date}
                        onChange={(e) => setFormData({ ...formData, death_date: e.target.value })}
                        data-testid="death-date-input"
                        className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-2 font-sans"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="service_date" className="font-sans text-foreground mb-2 block">
                      Memorial Service Date (Optional)
                    </Label>
                    <Input
                      id="service_date"
                      type="date"
                      value={formData.service_date}
                      onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                      data-testid="service-date-input"
                      className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-2 font-sans"
                    />
                  </div>

                  <div>
                    <Label className="font-sans text-foreground mb-3 block">
                      What would you like to call this gathering? (Optional)
                    </Label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {serviceTypeOptions.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setFormData({ ...formData, service_type: option })}
                          className={`px-4 py-2 rounded-full text-sm font-sans border transition-all ${
                            formData.service_type === option
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-white text-foreground border-border hover:border-primary'
                          }`}
                          data-testid={`service-type-${option.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    {formData.service_type === 'Other' && (
                      <Input
                        placeholder="Enter custom service name"
                        value={formData.custom_service_type}
                        onChange={(e) => setFormData({ ...formData, custom_service_type: e.target.value })}
                        data-testid="custom-service-input"
                        className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-2 font-sans"
                      />
                    )}
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-6 py-2 font-serif"
                    >
                      <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
                      Back
                    </Button>
                    <Button
                      onClick={() => setStep(3)}
                      data-testid="next-step-2"
                      className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-8 py-3 font-serif tracking-wide"
                    >
                      Continue
                      <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="host_message" className="font-sans text-foreground mb-2 block">
                      Share your thoughts about {formData.deceased_name} (Optional)
                    </Label>
                    <Textarea
                      id="host_message"
                      value={formData.host_message}
                      onChange={(e) => setFormData({ ...formData, host_message: e.target.value })}
                      data-testid="host-message-input"
                      className="bg-transparent border border-border focus:border-primary rounded-lg p-4 font-sans min-h-[150px]"
                      placeholder="What made them special? What will you remember most?"
                    />
                  </div>

                  <div className="bg-paper border border-border rounded-lg p-4">
                    <p className="text-sm font-sans text-foreground-muted mb-2">
                      <MessageCircle className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
                      After creating this memorial, you'll receive a unique link and shareable image.
                    </p>
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button
                      onClick={() => setStep(2)}
                      variant="outline"
                      className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-6 py-2 font-serif"
                    >
                      <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
                      Back
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      data-testid="create-memorial-submit"
                      className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-8 py-3 font-serif tracking-wide"
                    >
                      {submitting ? 'Creating...' : 'Create Memorial'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Success View */}
        {view === 'create' && step === 4 && createdMemorial && (
          <div className="bg-white border border-border shadow-card rounded-lg p-8 space-y-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-green-600" strokeWidth={1.5} />
              </div>
              <h2 className="text-3xl font-serif text-foreground mb-2">Memorial Created!</h2>
              <p className="text-foreground-muted font-sans">
                Share this memorial with family and friends
              </p>
            </div>

            {generatedImage && (
              <div className="space-y-4">
                <h3 className="text-lg font-serif text-foreground text-center">Shareable Memorial Card</h3>
                <div className="flex justify-center">
                  <img 
                    src={generatedImage} 
                    alt="Memorial card" 
                    className="max-w-sm w-full rounded-lg shadow-lg border border-border"
                  />
                </div>
                <div className="flex justify-center">
                  <Button
                    onClick={downloadImage}
                    variant="outline"
                    className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-6 py-2 font-serif"
                  >
                    <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Download Image
                  </Button>
                </div>
              </div>
            )}

            <div className="bg-paper border border-border rounded-lg p-6">
              <h3 className="text-lg font-serif text-foreground mb-3">Memorial Link</h3>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/tribute/${createdMemorial.memorial_id}`}
                  className="flex-1 bg-white border border-border rounded-lg px-4 py-2 text-sm font-mono"
                />
                <Button
                  onClick={() => copyLink(createdMemorial.memorial_id)}
                  className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-lg px-4 py-2"
                >
                  <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Copy
                </Button>
              </div>
            </div>

            <div className="flex justify-center space-x-4">
              <Button
                onClick={() => {
                  fetchMyMemorials();
                  setView('list');
                }}
                variant="outline"
                className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-6 py-2 font-serif"
              >
                Back to My Memorials
              </Button>
              <Button
                onClick={() => navigate(`/tribute/${createdMemorial.memorial_id}`)}
                className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-8 py-3 font-serif tracking-wide"
              >
                View Memorial Page
                <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
